import urlPaths, {
  url,
  matchPaths,
  workspaceIdFromUrl,
  TEAM_TAB,
} from "src/consts/urlPaths";
import getAllocationVisibility from "src/allocation/util/getAllocationVisibility";
import { getForecastTeamTab } from "src/allocation/pages/ForecastPageDynamic/isForecastV8Enabled";

// Planner variants share one resolver: which of the three is reachable depends
// on whether the group requests or approves numbers, not on which one the user
// happened to be viewing.
const remapPlannerTab = (tabType, allocationVisibility) => {
  if (allocationVisibility.requestNumbers2) {
    return tabType === TEAM_TAB.PLANNER
      ? TEAM_TAB.PLANNER
      : TEAM_TAB.REQUEST_ALLOCATION_NUMBERS;
  }

  if (allocationVisibility.approveNumbers2) {
    return TEAM_TAB.APPROVE_ALLOCATION_NUMBERS;
  }

  return tabType;
};

const remapForecastTab = (tabType, allocationVisibility, workspace) => {
  if (!allocationVisibility.forecastNumbers) {
    return tabType;
  }

  const flags = workspace?.config?.featureFlags ?? {};
  return getForecastTeamTab({
    enablePositionBackedVacancies: flags.enablePositionBackedVacancies,
    enableForecastPositionView: flags.enableForecastPositionView,
    enableForecastV8: flags.enableForecastV8,
  });
};

const remapIndividualAllocationsTab = (tabType, allocationVisibility) =>
  allocationVisibility.requestPeople ||
  allocationVisibility.individualAllocations
    ? TEAM_TAB.INDIVIDUAL_ALLOCATIONS
    : tabType;

// Tab types whose validity can only be determined via getAllocationVisibility
// (i.e. they need the group's permissions/type to know which allocation
// sub-tab, if any, is actually reachable). Matches side-nav remapping in
// useOnNodeSelected — Planner / Forecast / Team Builder (not the memberships
// "Allocations" timeline tab). A Map rather than an object literal so an
// unexpected tabType from the URL can't resolve through Object.prototype.
const ALLOCATION_TAB_REMAPS = new Map([
  [TEAM_TAB.PLANNER, remapPlannerTab],
  [TEAM_TAB.REQUEST_ALLOCATION_NUMBERS, remapPlannerTab],
  [TEAM_TAB.APPROVE_ALLOCATION_NUMBERS, remapPlannerTab],
  [TEAM_TAB.FORECAST, remapForecastTab],
  [TEAM_TAB.INDIVIDUAL_ALLOCATIONS, remapIndividualAllocationsTab],
]);

export const isAllocationTeamTab = (tabType) =>
  ALLOCATION_TAB_REMAPS.has(tabType);

const getCurrentTabType = (pathname) => {
  const match = matchPaths(pathname);

  if (match?.path !== urlPaths.TEAM_DETAILS) {
    return TEAM_TAB.DETAILS;
  }

  return match?.params?.tabType;
};

const getTabType = ({ allocationVisibility, pathname, workspace }) => {
  const tabType = getCurrentTabType(pathname);
  const remapTab = ALLOCATION_TAB_REMAPS.get(tabType);

  return remapTab
    ? remapTab(tabType, allocationVisibility, workspace)
    : tabType;
};

// `pathname` lets callers (e.g. breadcrumbs, which already have it from
// useLocation()) pass the current route explicitly so the "stay on the same
// tab" check doesn't depend on window.location (which can lag/mismatch in
// tests using MemoryRouter, or with in-page navigation state). Defaults to
// window.location.pathname (via matchPaths) when omitted, matching the
// side-nav tree's original behaviour.
const getTeamPath = ({
  group,
  workspace,
  activeAllocationProject,
  pathname,
}) => {
  const allocationVisibility = getAllocationVisibility({
    group,
    config: workspace?.config,
    activeAllocationProject,
  });

  const tabType = getTabType({
    allocationVisibility,
    pathname,
    workspace,
  });

  const teamPath = url(urlPaths.TEAM_DETAILS, {
    id: group?.id,
    tabType,
    // When an explicit pathname is supplied, derive workspaceId from that
    // same pathname rather than letting `url()` fall back to
    // window.location — otherwise a caller passing a pathname that differs
    // from the current window URL (breadcrumbs, MemoryRouter-based tests)
    // could get a URL with a mismatched/missing workspaceId.
    ...(pathname ? { workspaceId: workspaceIdFromUrl(pathname) } : {}),
  });

  return teamPath;
};

export default getTeamPath;
